import React, { Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { showToast } from "../../../components/ToastifyNotification";
import Breadcrumb from "../../../components/common/breadcrumb";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BirthReportApi } from "../../../api/ipd/ipdReportsAPI";

const BirthReport = () => {
    const [filteredData, setFilteredData] = useState([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [search, setSearch] = useState("");

    const dispatch = useDispatch();

    const fetchData = async () => {
        dispatch({ type: "LOADER_START" });

        try {
            const requestData = {
                fromDate,
                toDate,
                search,
            };

            const response = await BirthReportApi(requestData);

            if (response.success) {
                showToast("success", response.message);

                const formattedData = response.data.map((item, index) => ({
                    index: index + 1,
                    id: item._id,
                    ipdNo: item.ipdEntryId?.ipdNo || "",
                    uhidNo: item.ipdEntryId?.uhidNo || "",
                    rowDeliveryDate: item.deliveryDate || "",
                    deliveryDate: item.deliveryDate
                        ? new Date(item.deliveryDate).toLocaleDateString("en-IN")
                        : "",
                    patientName:
                        (item.ipdEntryId?.patientNamePrefix ? item.ipdEntryId?.patientNamePrefix + " " : "") +
                        (item.ipdEntryId?.patientName || ""),
                    sex: item.ipdEntryId?.sex || "",
                    ageValue: item.ipdEntryId?.age || 0,
                    age: item.ipdEntryId?.age ? `${item.ipdEntryId?.age} ${item.ipdEntryId?.ageSuffix || ""}` : "",
                    mobileNo: item.ipdEntryId?.mobileNo || "",
                    address: item.ipdEntryId?.address || "",

                    doctor1: item.doctorId1?.name || "",
                    doctor2: item.doctorId2?.name || "",
                    aneasthetist: item.aneasthetist || "",
                    assistant: item.assistant || "",
                    type: item.type || "",
                    patientStatus: item.patientStatus || "",
                    pregancyInWeek: item.pregancyInWeek || "",
                    bornChild: item.bornChild || "",
                    aliveChild: item.aliveChild || "",
                    deadChild: item.deadChild || "",
                    lastChildAge: item.lastChildAge || "",
                    lastChildSex: item.lastChildSex || "",
                    totalLivingChild: item.totalLivingChild || "",
                    remark: item.remark || "",

                    // ===========================
                    // CHILD DETAILS FLATTEN
                    // ===========================
                    childDetails: item.childDetails?.length
                        ? item.childDetails
                            .map(
                                (c, i) =>
                                    `#${i + 1} Name: ${c.name || ""}, Sex: ${c.sex || ""
                                    }, Weight: ${c.weight || ""}${c.unit ? " " + c.unit : ""
                                    }, Time: ${c.time || ""}, Date: ${c.date
                                        ? new Date(c.date).toLocaleDateString("en-IN")
                                        : ""
                                    }`
                            )
                            .join("\n")
                        : "",

                    // ===========================
                    // MOTHER DETAILS FLATTEN
                    // ===========================
                    motherDetails: item.monther
                        ? `Age At Marriage: ${item.monther.ageAtMarriage || ""}\nNationality: ${item.monther.nationality || ""
                        }\nReligion: ${item.monther.religion || ""}\nQualification: ${item.monther.qualification || ""
                        }\nOccupation: ${item.monther.occupation || ""}`
                        : "",

                    // ===========================
                    // FATHER DETAILS FLATTEN
                    // ===========================
                    fatherDetails: item.father
                        ? `Name: ${item.father.fatherName || ""}\nAge: ${item.father.age || ""
                        }\nNationality: ${item.father.nationality || ""
                        }\nReligion: ${item.father.religion || ""}\nQualification: ${item.father.qualification || ""
                        }\nOccupation: ${item.father.occupation || ""}`
                        : "",

                    createdBy: item.createdBy?.name || "",
                    createdAt: item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("en-IN")
                        : "",
                    updatedBy: item.updatedBy?.name || "",
                    updatedAt: item.updatedAt
                        ? new Date(item.updatedAt).toLocaleDateString("en-IN")
                        : "",
                }));

                setFilteredData(formattedData);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    // ===========================
    // TABLE HEADERS
    // ===========================

    const headers = [
        { label: "S.No", key: "index" },
        { label: "IPD No", key: "ipdNo" },
        { label: "UHID No", key: "uhidNo" },
        { label: "Delivery Date", key: "deliveryDate" },
        { label: "Patient Name", key: "patientName" },
        { label: "Sex", key: "sex" },
        { label: "Age", key: "age" },
        { label: "Mobile No", key: "mobileNo" },
        { label: "Address", key: "address" },

        { label: "Doctor 1", key: "doctor1" },
        { label: "Doctor 2", key: "doctor2" },
        { label: "Anesthetist", key: "aneasthetist" },
        { label: "Assistant", key: "assistant" },
        { label: "Type", key: "type" },

        { label: "Pregnancy (Weeks)", key: "pregancyInWeek" },
        { label: "Born Children", key: "bornChild" },
        { label: "Alive Children", key: "aliveChild" },
        { label: "Dead Children", key: "deadChild" },
        { label: "Total Living Child", key: "totalLivingChild" },

        { label: "Child Details", key: "childDetails" },
        { label: "Mother Details", key: "motherDetails" },
        { label: "Father Details", key: "fatherDetails" },

        { label: "Remark", key: "remark" },

        { label: "Created By", key: "createdBy" },
        { label: "Created Date", key: "createdAt" },
        { label: "Updated By", key: "updatedBy" },
        { label: "Updated Date", key: "updatedAt" },
    ];

    const columns = headers.map((h) => ({
        name: h.label,
        selector: (row) => row[h.key],
        sortable: true,
        wrap: true,
        width: "200px",
    }));

    // ===========================
    // EXPORT EXCEL
    // ===========================

    const exportToExcel = () => {
        const sheetData = filteredData.map((row) => {
            let obj = {};
            headers.forEach((h) => (obj[h.label] = row[h.key]));
            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Birth Report");
        XLSX.writeFile(workbook, "Birth_Report.xlsx");
    };

    // ===========================
    // EXPORT PDF
    // ===========================

    const exportToPDF = () => {
        const doc = new jsPDF({ orientation: "landscape" });
        doc.text("Birth Report", 14, 10);

        const tableColumn = headers.map((h) => h.label);
        const tableRows = filteredData.map((row) => headers.map((h) => row[h.key]));

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            styles: { cellWidth: "wrap" },
        });

        doc.save("Birth_Report.pdf");
    };

    const resetFilters = () => {
        setFromDate("");
        setToDate("");
        setSearch("");
        setFilteredData([]);
    };

    return (
        <Fragment>
            <Breadcrumb parent="IPD" title="Birth Report" />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <h5>Birth Report</h5>

                                <div className="row mt-3">
                                    <div className="col-sm-2">
                                        <label>From Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-2">
                                        <label>To Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-3">
                                        <label>Search</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search..."
                                        />
                                    </div>

                                    <div className="col-sm mt-4">
                                        <button
                                            className="btn btn-primary me-2"
                                            onClick={fetchData}
                                        >
                                            Search
                                        </button>
                                        <button
                                            className="btn btn-warning"
                                            onClick={resetFilters}
                                        >
                                            Reset
                                        </button>
                                    </div>

                                    <div className="col-sm mt-4 text-end">
                                        <button
                                            className="btn btn-success me-2"
                                            onClick={exportToExcel}
                                        >
                                            Excel
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            onClick={exportToPDF}
                                        >
                                            PDF
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    pagination
                                    highlightOnHover
                                    dense
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default BirthReport;
